import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [authLoading, setAuthLoading] = useState(true); // Status loading

  const auth = getAuth();

  useEffect(() => {
    // Safety timeout: jika Firebase tidak merespons dalam 3 detik, hentikan loading
    const safetyTimeout = setTimeout(() => {
      setAuthLoading((currentLoading) => {
        if (currentLoading) {
          console.warn("Auth check timed out, forcing loading to false.");
          return false;
        }
        return currentLoading;
      });
    }, 3000);

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Jangan clear timeout di sini, biarkan berjalan sampai proses selesai
      try {
        if (!currentUser) {
          // Pengguna logout
          setUser(null);
          setRoles([]);
          setPhoneNumber('');
        } else {
          // Pengguna login, ambil data peran dari Firestore
          try {
            // Gunakan Promise.race untuk membatasi waktu tunggu Firestore
            const fetchUserPromise = async () => {
              const userDocRef = doc(db, 'users', currentUser.uid);
              const userDoc = await getDoc(userDocRef);
              return userDoc;
            };

            // Timeout untuk Firestore request (2 detik)
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Firestore timeout")), 2000)
            );

            const userDoc = await Promise.race([fetchUserPromise(), timeoutPromise]);

            if (userDoc.exists()) {
              setRoles(userDoc.data().roles || ['member']);
            } else {
              // Jika dokumen pengguna belum ada, buat dengan peran default 'member'
              await setDoc(doc(db, 'users', currentUser.uid), {
                roles: ['member'],
                phoneNumber: currentUser.phoneNumber,
                createdAt: serverTimestamp(),
                phoneNumberVerifiedAt: serverTimestamp()
              });
              setRoles(['member']);
            }
          } catch (error) {
            console.error("Error fetching user data or timeout:", error);
            // Fallback status jika terjadi error atau timeout
            setRoles(['member']);
          }
          setUser(currentUser);
          setPhoneNumber(currentUser.phoneNumber);
        }
      } catch (err) {
        console.error("Auth Error:", err);
      } finally {
        // Clear timeout dan tandai selesai
        clearTimeout(safetyTimeout);
        setAuthLoading(false);
      }
    });

    return () => {
      clearTimeout(safetyTimeout);
      unsubscribe();
    };
  }, [auth]);

  const verifyOtp = async (confirmationResult, otp) => {
    if (!confirmationResult) throw new Error("Hasil konfirmasi tidak tersedia.");
    return await confirmationResult.confirm(otp);
  };

  const signOut = () => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
    firebaseSignOut(auth);
  };

  const value = { user, roles, phoneNumber, auth, authLoading, verifyOtp, signOut };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
